// noinspection ExceptionCaughtLocallyJS

import axios, { AxiosResponse } from 'axios';
import store from '../store/store';
import router from '../router/router';
import { logHelper } from '@/helpers/log-helper';
import { actions as _act } from '@/models/store.actions';
import 'url-polyfill';
import merge from 'lodash/merge';
import { AppError } from '@/models/log.models';
import { isIE } from 'mobile-device-detect';
import { ipDataBaseUrl } from '@/services/external.service';
import { isNativeApp, getNativePlatform } from '@/helpers/platform-helper';
import { logger } from '@/helpers/log.service';
import { isSignFileView, isUnregUserView } from '@/helpers/ui-helper';
import { AppHttpHeaders, RouterView } from '@/models/enums';
import { getRecaptchaToken } from '@/helpers/recaptha-helper';
import { getLocale } from '@/helpers';
import { EventBus } from '@/helpers/event-bus';

let model = {
  apiHost: undefined,
  get appBaseUrl() {
    return window.location.origin;
  },
  async getApiHost() {
    if (typeof this.apiHost === 'undefined') {
      const cfg = await store.getters[_act.SETT_GET_CFG_WAITER];
      this.apiHost = cfg.apiHost || '';
    }
    return this.apiHost;
  }
};

const isNativeAppVar = isNativeApp();
const nativePlatform = getNativePlatform();
let refreshTokenPromise;

function urlAllowRefreshToken(url) {
  return (
    url &&
    !/(static\/config.json)|(api.*settings$)|(Auth\/RefreshToken$)|(Auth\/Register$)|(Auth\/ConfirmEmailRequest$)|(Auth\/ConfirmEmail$)|(Auth\/ResetPasswordRequest$)|(Auth\/ResetPassword$)|(Rouming\/AOSystemUrl$)/i.test(
      url
    )
  );
}

function isUnregUserApi(url): boolean {
  return url && /unregistered-users\/documents/i.test(url);
}

async function mapError(err) {
  let error;
  const { response } = err;
  if (response) {
    error = { status: response.status, statusText: response.statusText };
    if ('data' in response) {
      if (typeof response.data === 'string') {
        error = merge(error, { message: response.data });
      } else if (isJsonBlob(response.data)) {
        const json = await getJsonFromBlob(response.data) as string;
        let err2 = JSON.parse(json);
        error = merge(error, err2);
      } else if (!('message' in response.data) && 'config' in response && 'url' in response.config) {
        error = merge(error, { message: 'Error on requesting URL ' + response.config.url });
      } else {
        error = merge(error, response.data);
      }
    }
  } else {
    error = err;
  }
  return error;
}

function isJsonBlob(maybeBlob) {
  return 'size' in maybeBlob && typeof maybeBlob.size === 'number' && maybeBlob.size > 0 &&
    'type' in maybeBlob && typeof maybeBlob.type === 'string' && maybeBlob.type === 'application/json';
}

function getJsonFromBlob(blob) {
  return new Promise((resolve, reject) => {
    let reader = new FileReader();
    reader.onload = () => { resolve(reader.result); };
    reader.onerror = reject;
    reader.readAsText(blob);
  });
}

function addHeadersToRequest(request) {
  if (isNativeAppVar) {
    request.headers['ao-native-client-platform'] = nativePlatform;
  }
  request.headers['Accept-Language'] = store.getters[_act.SETT_GET_LOCALEGET];
  if (isIE) {
    request.headers['Pragma'] = 'no-cache';
  }
}

async function modifyRequestUrl(request) {
  if (request.url) {
    if (isNativeAppVar) {
      if (/.*api\/.*/.test(request.url)) {
        const apiHost = await model.getApiHost();
        request.url = `${apiHost}/${request.url}`;
      }
    } else {
      request.url = new URL(request.url, model.appBaseUrl).href;
    }
  }
}

function isSkipLoggingError(err: AppError): boolean {
  let skip = false;
  if (err &&
      (err.code === 'AppDocumentReceiptConfirmRequiredException' ||
       err.isKeyEdrpouMismatchError ||
       err.isInternalNestedSignatureChangedDuringSigningError)) {
    skip = true;
  }
  return skip;
}

function isNavigateToLoginOnUnauthorized(): boolean {
  return !isSignFileView() && !isUnregUserView();
}

let isNavigationToLoginPageTriggered = false;

function handleResponseHeaders(response: AxiosResponse) {
  if (response && response.headers) {
    if (AppHttpHeaders.ServerVersion in response.headers) {
      store.commit(_act.SETT_MUT_SERV_VERS, response.headers[AppHttpHeaders.ServerVersion]);
    }
    if (AppHttpHeaders.ServerTime in response.headers) {
      store.commit(_act.SETT_MUT_SERV_TIME, response.headers[AppHttpHeaders.ServerTime]);
    }
    if (AppHttpHeaders.SignLibFilesVersion in response.headers) {
      store.commit(_act.SETTTEMP_MUT_SIGNLIBFILESSERVERVERSION, response.headers[AppHttpHeaders.SignLibFilesVersion]);
    }
  }
}

export default {
  install(Vue, options) {
    axios.interceptors.request.use(
      config => {
        return new Promise(async(resolve, reject) => {
          try {
            if (new RegExp(ipDataBaseUrl).test(config.url || '')) {
              resolve(config);
              return;
            }

            addHeadersToRequest(config);

            await modifyRequestUrl(config);

            let tokens = store.getters[_act.AUTH_GET_TOKENSGET];

            let shouldRefreshToken =
            !isSignFileView() &&
            !isUnregUserView() &&
            !!tokens.refresh &&
            (!tokens.access || (tokens.expireaccessdate && new Date() > new Date(tokens.expireaccessdate))) &&
            urlAllowRefreshToken(config.url);
            if (shouldRefreshToken) {
              if (!refreshTokenPromise) {
                refreshTokenPromise = store.dispatch(_act.AUTH_ACT_REFRESHTOKEM);
              }

              try {
                await refreshTokenPromise;
              } catch (refreshErr) {
                if (refreshErr instanceof AppError && refreshErr.isUnauthorized) {
                  throw new axios.Cancel(getLocale('errors.refreshingTokenCancelled').toString());
                }
                throw refreshErr;
              } finally {
                refreshTokenPromise = null;
              }

              tokens = store.getters[_act.AUTH_GET_TOKENSGET];
            }

            if (!config.headers) {
              config.headers = {};
            }

            if (isSignFileView()) {
              config.headers.authorization = 'Bearer ' + store.getters[_act.SETTTEMP_GET_ACCESSTOKEN];
            } else if (isUnregUserApi(config.url)) {
              config.headers[AppHttpHeaders.UnregUserToken] = store.getters[_act.SETTTEMP_GET_UNREGUSERTOKEN];
              if (!(AppHttpHeaders.RecaptchaToken in config.headers)) {
                const recaptchaResult = await getRecaptchaToken();
                if (recaptchaResult.isCancelled) {
                  throw new Error(getLocale('errors.unauthorized').toString());
                } else if (recaptchaResult.isError) {
                  throw new Error(recaptchaResult.errorDescription);
                } else {
                  config.headers[AppHttpHeaders.RecaptchaType] = recaptchaResult.recaptchaType;
                  config.headers[AppHttpHeaders.RecaptchaToken] = recaptchaResult.recaptchaToken;
                }
              }
            } else if (tokens.access) {
              config.headers.authorization = 'Bearer ' + tokens.access;
            }

            resolve(config);
          } catch (error) {
            reject(error);
          }
        });
      },
      err => {
        return Promise.reject(err);
      }
    );

    axios.interceptors.response.use(
      response => {
        handleResponseHeaders(response);

        return response;
      },
      async error => {
        let err = new AppError(await mapError(error));
        if (!axios.isCancel(error)) {
          if (err) {
            if (err.isUnauthorized) {
              if (isNavigateToLoginOnUnauthorized()) {
                logger.warn('response error', err);
                store.commit(_act.AUTH_MUT_LOGOUT);
                EventBus.$emit('user-logged-out');
                if (router.currentRoute.name !== RouterView.Login && !isNavigationToLoginPageTriggered) {
                  isNavigationToLoginPageTriggered = true;
                  const onNavigationEnded = () => (isNavigationToLoginPageTriggered = false);
                  router.push({ name: RouterView.Login }, onNavigationEnded, onNavigationEnded);
                }
              } else {
                if (isUnregUserView()) {
                  logHelper.error(err);
                }
              }
            } else if (!isSkipLoggingError(err)) {
              logHelper.error(err);
            }
          } else {
            logger.warn('response unknown error', error);
          }
        }

        return Promise.reject(err);
      }
    );
  }
};
